import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { TrendingUp, Search, Calendar, Loader2, BarChart3 } from "lucide-react";
import { toastApiError } from "../../lib/toastApiError";

export default function MarketData() {
  const { t } = useTranslation("pharmacy");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);

  const periodLabels = {
    day: t("marketData.today"),
    week: t("marketData.thisWeek"),
    month: t("marketData.thisMonth"),
  };

  const fetchMarketData = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/pharmacy/market-data?period=${period}`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching market data:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarketData();
  }, [period]);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="market-data-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("marketData.title")}</h1>
          <p className="text-slate-500 mt-1">{t("marketData.subtitle")}</p>
        </div>

        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[180px]" data-testid="period-select">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">{t("marketData.today")}</SelectItem>
            <SelectItem value="week">{t("marketData.thisWeek")}</SelectItem>
            <SelectItem value="month">{t("marketData.thisMonth")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Search className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("marketData.totalSearches")}</p>
                    <p className="text-2xl font-bold">{data.total_searches}</p>
                    <p className="text-xs text-slate-400">{periodLabels[period]}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("marketData.uniqueMedicines")}</p>
                    <p className="text-2xl font-bold">{data.top_medicines?.length || 0}</p>
                    <p className="text-xs text-slate-400">{t("marketData.searched")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-omu-blue-500" />
                {t("marketData.topMedicines")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_medicines && data.top_medicines.length > 0 ? (
                <div className="space-y-3">
                  {data.top_medicines.map((med, index) => (
                    <div
                      key={med.name}
                      className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
                      data-testid={`medicine-row-${index}`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                        index === 0 ? "bg-yellow-400 text-yellow-900" :
                        index === 1 ? "bg-slate-300 text-slate-700" :
                        index === 2 ? "bg-orange-300 text-orange-800" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {index + 1}
                      </div>

                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{med.name}</p>
                      </div>

                      <div className="text-right">
                        <Badge className="bg-omu-blue-100 text-omu-blue-700">
                          {t("marketData.searchCount", { count: med.count })}
                        </Badge>
                      </div>

                      <div className="w-24 hidden sm:block">
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-omu-blue-500 rounded-full"
                            style={{ width: `${(med.count / data.top_medicines[0].count) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Search className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>{t("marketData.noDataPeriod")}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-omu-blue-50 to-blue-50 border-omu-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-omu-blue-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-omu-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-omu-blue-900">{t("marketData.strategicTip")}</h3>
                  <p className="text-sm text-omu-blue-700 mt-1">
                    {t("marketData.strategicDesc")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500">
          {t("marketData.noData")}
        </div>
      )}
    </div>
  );
}
